import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Layers } from "lucide-react";

import { getPaginated } from "@/lib/api-client";
import { formatDateTime } from "@/lib/format";
import { Card, CardBody } from "@/components/ui/Card";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { Button } from "@/components/ui/Button";
import { Pagination } from "@/components/ui/Pagination";
import type { ClubEntry } from "@/types/club";

// Helper function to convert number to 1st, 2nd, 3rd, 4th etc.
function getOrdinalClubName(n: number) {
  const j = n % 10;
  const k = n % 100;
  if (j === 1 && k !== 11) return n + "st club";
  if (j === 2 && k !== 12) return n + "nd club";
  if (j === 3 && k !== 13) return n + "rd club";
  return n + "th club";
}

export function EntriesPage() {
  const [page, setPage] = useState(1);
  const pageSize = 10; // এক পেজে কয়টি ক্লাব দেখাবে

  // 🛠️ FIXED: Calling backend with order="asc" so the database does the sorting!
  const { data, isLoading } = useQuery({
    queryKey: ["entries", page],
    queryFn: () => getPaginated<ClubEntry>("/club/entries/entries", { 
      page, 
      page_size: pageSize,
      order: "asc" // This tells the backend to send Oldest First
    }),
  });

  const entries = data?.results ?? [];

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-surface-900">My Clubs</h1>
        <p className="text-sm text-surface-500 mt-1">
          View your club progress and details.
        </p>
      </header>

      <Card>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-5 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
            </div>
          ) : entries.length > 0 ? (
            <>
              <Table>
                <THead>
                  <TR>
                    <TH>Clubs</TH>
                    <TH>Serial</TH>
                    <TH>Date</TH>
                    <TH className="text-right">Action</TH>
                  </TR>
                </THead>
                <tbody>
                  {entries.map((e, index) => {
                    // 🛠️ FIXED: Mathematical serial calculation based on pagination.
                    // Page 1: (1-1)*10 + 0 + 1 = 1 (1st club)
                    // Page 2: (2-1)*10 + 0 + 1 = 11 (11th club)
                    const clubNumber = ((page - 1) * pageSize) + index + 1;
                    
                    return (
                      <TR key={e.entry_id}>
                        <TD className="font-semibold text-surface-900 capitalize">
                          {getOrdinalClubName(clubNumber)}
                        </TD>
                        <TD className="font-mono text-xs text-surface-600">
                          {e.entry_id}
                        </TD>
                        <TD className="text-sm text-surface-800">
                          {formatDateTime(e.created_at).split(' •')[0]}
                        </TD>
                        <TD className="text-right">
                          <Link to={`/dashboard/entries/${e.entry_id}`}>
                            <Button size="sm" variant="primary">
                              More
                            </Button>
                          </Link>
                        </TD>
                      </TR>
                    );
                  })}
                </tbody>
              </Table>
              
              {/* Pagination Component */}
              {(data?.num_pages ?? 1) > 1 && (
                <div className="border-t border-surface-200">
                  <Pagination
                    page={page}
                    numPages={data!.num_pages}
                    onChange={setPage}
                  />
                </div>
              )}
            </>
          ) : (
            <EmptyState icon={Layers} title="No Clubs yet"
                         description="Reach the qualification threshold or purchase a membership to create one." />
          )}
        </CardBody>
      </Card>
    </div>
  );
}